import type { WorkspaceMember } from '@protoss/shared';
import { createDomainRequestSchema, updateDomainRequestSchema } from '@protoss/shared';
import { Button, MultiSelect, TextArea, TextField } from '@protoss/ui';
import * as React from 'react';
import { useDirtyState } from '../../hooks/useDirtyState';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';

interface DomainFormProps {
  mode: 'create' | 'edit';
  initialData?: {
    name: string;
    description?: string | null;
  };
  /** All workspace members available for selection */
  workspaceMembers?: WorkspaceMember[];
  /**
   * Admins already persisted in the database - passed in edit mode for status tracking.
   * In create mode, omit this prop.
   */
  initialAdmins?: Array<{ uuid: string; displayName: string | null; email: string }>;
  onSubmit: (data: {
    name?: string;
    description?: string;
    domainAdminUuids?: string[];
  }) => Promise<void>;
  onCancel: () => void;
}

/**
 * DomainForm - reusable form for creating and editing domains
 *
 * Features:
 * - Client-side validation using Zod schemas
 * - Inline error display
 * - Create and edit modes
 * - Loading state during submission
 * - Create mode requires selecting a domain admin from workspace members
 *
 * Usage:
 * ```tsx
 * // Create mode
 * <DomainForm
 *   mode="create"
 *   workspaceMembers={members}
 *   onSubmit={handleCreate}
 *   onCancel={handleCancel}
 * />
 *
 * // Edit mode
 * <DomainForm
 *   mode="edit"
 *   initialData={{ name: 'Domain A', description: 'Description' }}
 *   onSubmit={handleUpdate}
 *   onCancel={handleCancel}
 * />
 * ```
 */
export function DomainForm({
  mode,
  initialData,
  workspaceMembers = [],
  initialAdmins,
  onSubmit,
  onCancel,
}: DomainFormProps) {
  const [name, setName] = React.useState(initialData?.name ?? '');
  const [description, setDescription] = React.useState(initialData?.description ?? '');
  const [selectedAdminUuids, setSelectedAdminUuids] = React.useState<string[]>(
    () => initialAdmins?.map((a) => a.uuid) ?? []
  );
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { isDirty, setBaseline, updateCurrent } = useDirtyState<{
    name: string;
    description: string;
    adminUuids: string[];
  }>({ trimStrings: true });

  const baselinePayload = React.useMemo(
    () => ({
      name: initialData?.name ?? '',
      description: initialData?.description ?? '',
      adminUuids: [...(initialAdmins?.map((a) => a.uuid) ?? [])].sort(),
    }),
    [initialAdmins, initialData?.description, initialData?.name]
  );

  const currentPayload = React.useMemo(
    () => ({
      name,
      description,
      adminUuids: [...selectedAdminUuids].sort(),
    }),
    [description, name, selectedAdminUuids]
  );

  React.useEffect(() => {
    setBaseline(baselinePayload);
  }, [baselinePayload, setBaseline]);

  React.useEffect(() => {
    updateCurrent(currentPayload);
  }, [currentPayload, updateCurrent]);

  useUnsavedChanges(!isSubmitting && isDirty);

  const schema = mode === 'create' ? createDomainRequestSchema : updateDomainRequestSchema;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Build data object based on mode
    const data =
      mode === 'create'
        ? { name, description: description || undefined, domainAdminUuids: selectedAdminUuids }
        : {
            name,
            description: description || undefined,
            ...(selectedAdminUuids.length > 0 ? { domainAdminUuids: selectedAdminUuids } : {}),
          };

    const result = schema.safeParse(data);

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as string;
        fieldErrors[field] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    // Submit
    try {
      setIsSubmitting(true);
      await onSubmit(
        result.data as { name?: string; description?: string; domainAdminUuids?: string[] }
      );
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ submit: error instanceof Error ? error.message : 'Submission failed' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="domain-form" onSubmit={handleSubmit}>
      <div className="domain-form__fields">
        <TextField
          label="Domain Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Engineering, Marketing"
          error={errors.name}
          disabled={isSubmitting}
          required
        />

        <TextArea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description of this domain"
          error={errors.description}
          disabled={isSubmitting}
          rows={4}
        />

        {/* Always render so the field height is stable before and after member fetch.
           In edit mode, pre-seed options from initialAdmins so status list is
           immediate. In create mode, show loading skeleton until members arrive. */}
        <MultiSelect
          options={
            workspaceMembers.length > 0
              ? workspaceMembers.map((m) => ({
                  value: m.uuid,
                  label: m.displayName ?? m.email,
                  sublabel: m.displayName ? m.email : undefined,
                }))
              : (initialAdmins ?? []).map((a) => ({
                  value: a.uuid,
                  label: a.displayName ?? a.email,
                  sublabel: a.displayName ? a.email : undefined,
                }))
          }
          selectedValues={selectedAdminUuids}
          originalValues={initialAdmins?.map((a) => a.uuid)}
          onSelectionChange={setSelectedAdminUuids}
          label="Domain Administrators"
          placeholder="Type to search members…"
          loading={workspaceMembers.length === 0 && !initialAdmins?.length}
          required={mode === 'create'}
          disabled={isSubmitting}
          error={errors.domainAdminUuids}
        />
      </div>

      {errors.submit && (
        <div className="domain-form__error" role="alert">
          {errors.submit}
        </div>
      )}

      <div className="domain-form__actions">
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create Domain' : 'Save Changes'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
